import {GetAnalysisPipelineStep, GetAnalysisStepData} from 'analysis-tool/AnalysisTypes';
import {
  LINEAGE_TERMINAL_STEP_TYPES,
  getTableLineageDescendantInformation,
  getTableLineageAncestorInformation,
  TableLineageType,
} from 'analysis-tool/data/DatasetLineageUtils';
import {isTopLevelTableStartStepType} from 'analysis-tool/data/LineageStepTypes';

import getLegacyStepFromAnalysis from '../analysis-tool/data/getStepFromAnalysis';
import {PipelineGraphQL} from './PipelineTypes';

/**
 * Given an pipeline and a step, return all children steps of that step
 */
export function getChildSteps(
  pipeline: PipelineGraphQL,
  step: GetAnalysisPipelineStep
): Array<GetAnalysisPipelineStep> {
  return step.definition.outgoingEdges.map((edge) => getLegacyStepFromAnalysis(pipeline, edge.child.step.id));
}

/**
 * Gather all the descendants in a single dataset lineage (inclusive).
 *
 * @deprecated This util makes dangerous assumptions about the shape of the DAG
 */
export function getTableLineageDescendantSteps(
  pipeline: PipelineGraphQL,
  step: GetAnalysisPipelineStep
): Array<GetAnalysisPipelineStep> {
  return getTableLineageDescendantInformation(pipeline, {step}).nodes.map((lineageNode) => lineageNode.step);
}

/**
 * Gather all the ancestors in a single dataset lineage (inclusive).
 *
 * @deprecated This util makes dangerous assumptions about the shape of the DAG
 */
export function getTableLineageAncestorSteps(
  pipeline: PipelineGraphQL,
  step: GetAnalysisPipelineStep
): Array<GetAnalysisPipelineStep> {
  return getTableLineageAncestorInformation(pipeline, {step}).nodes.map((lineageNode) => lineageNode.step);
}

function stepDatasContainDataFrame(stepDatas: Array<GetAnalysisStepData>, dataFrameId: string): boolean {
  return stepDatas.some(({dataFrames}) =>
    dataFrames.some(({id: otherDataFrameId}) => otherDataFrameId === dataFrameId)
  );
}

/**
 * Given an pipeline, a step, and a step data, return all children steps that have an edge to that step data
 */
export function getChildStepsForDataFrame(
  pipeline: PipelineGraphQL,
  step: GetAnalysisPipelineStep,
  dataFrameId: string
): Array<GetAnalysisPipelineStep> {
  if (!stepDatasContainDataFrame(step.outputStepDatas, dataFrameId)) {
    return [];
  }

  return getChildSteps(pipeline, step).filter((childStep) =>
    stepDatasContainDataFrame(childStep.inputStepDatas, dataFrameId)
  );
}

/**
 * Given an pipeline and a step, return all parent steps of that step
 */
export function getParentSteps(
  pipeline: PipelineGraphQL,
  step: GetAnalysisPipelineStep
): Array<GetAnalysisPipelineStep> {
  return step.definition.incomingEdges.map((edge) =>
    getLegacyStepFromAnalysis(pipeline, edge.parent.step.id)
  );
}

function getUpstreamStepsHelper(
  pipeline: PipelineGraphQL,
  step: GetAnalysisPipelineStep,
  seenStepIds: Set<string>
): Array<GetAnalysisPipelineStep> {
  const parentSteps = getParentSteps(pipeline, step).filter((step) => !seenStepIds.has(step.id));
  parentSteps.forEach((step) => seenStepIds.add(step.id));
  return [
    ...parentSteps,
    ...parentSteps.flatMap((step) => getUpstreamStepsHelper(pipeline, step, seenStepIds)),
  ];
}

/**
 * Given a pipeline and step, return all ancestor steps of that step.
 */
export function getUpstreamSteps(
  pipeline: PipelineGraphQL,
  step: GetAnalysisPipelineStep
): Array<GetAnalysisPipelineStep> {
  return getUpstreamStepsHelper(pipeline, step, new Set([step.id]));
}

/**
 * Given an pipeline and a step, return the origin step of the lineage.
 */
export function getOriginOrTerminalStep(
  pipeline: PipelineGraphQL,
  step: GetAnalysisPipelineStep
): GetAnalysisPipelineStep {
  return step.definition.incomingEdges.length === 0 ||
    LINEAGE_TERMINAL_STEP_TYPES.includes(step.definition.stepType)
    ? step
    : getOriginOrTerminalStep(pipeline, getParentSteps(pipeline, step)[0]);
}

/**
 * If a step is not a top level step, return the top level start step it is under. If the step is a top level step, return null.
 *
 * @deprecated This util makes dangerous assumptions about the shape of the DAG
 */
export function getTopLevelTableStartStepForViewLevelStep(
  pipeline: PipelineGraphQL,
  step: GetAnalysisPipelineStep
): GetAnalysisPipelineStep | null {
  const ancestorLineage = getTableLineageAncestorInformation(pipeline, {step});
  if (ancestorLineage.type === TableLineageType.TOP_LEVEL_TABLE) {
    return null;
  }
  const viewStep = ancestorLineage.nodes[0].step;
  const viewStepParent = getParentSteps(pipeline, viewStep)[0];
  const topLevelSteps = getTableLineageAncestorSteps(pipeline, viewStepParent);
  const topLevelStartStep = topLevelSteps.find((step) =>
    isTopLevelTableStartStepType(step.definition.stepType)
  );
  return topLevelStartStep!;
}

export function getRootAncestorSteps(
  pipeline: PipelineGraphQL,
  step: GetAnalysisPipelineStep
): Array<GetAnalysisPipelineStep> {
  const rootSteps = [];
  const stepStack = [step];
  while (stepStack.length > 0) {
    const currentStep = stepStack.pop()!;
    const parentSteps = getParentSteps(pipeline, currentStep);
    if (parentSteps.length === 0) {
      rootSteps.push(currentStep);
    }
    stepStack.push(...parentSteps);
  }
  return rootSteps;
}
